"use client";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useFilter } from "@/app/context/FilterContext";

// Each slot item is 1.6em tall; 5 items visible (8em container).
// CSS em units scale with the passed fontSize automatically.
const ITEM_EM = 1.6;
const VISIBLE = 5;

interface Props {
  filters: string[];
  fontSize: string | number;
  className?: string;
}

const WheelSelect = ({ filters, fontSize, className = "" }: Props) => {
  const { activeFilter, setActiveFilter, setIsWheelOpen, setFilters, noFilterLabel } = useFilter();

  // Seed the context filter list so the logo dropdown is populated immediately
  useEffect(() => {
    if (filters.length > 0) setFilters(filters);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // allOptions: noFilterLabel first, then real filters
  const allOptions = [noFilterLabel, ...filters];

  const [isOpen, setIsOpen] = useState(false);
  const [tempIdx, setTempIdx] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const dragStartIdx = useRef(0);

  const displayText = activeFilter ?? noFilterLabel;
  const fsStyle = typeof fontSize === "number" ? `${fontSize}rem` : fontSize;

  function open() {
    if (!allOptions.length) return;
    const idx = activeFilter ? Math.max(1, allOptions.indexOf(activeFilter)) : 0;
    setTempIdx(idx);
    setIsOpen(true);
    setIsWheelOpen(true);
  }

  function confirm(idx: number) {
    const selected = allOptions[idx];
    setActiveFilter(selected === noFilterLabel ? null : (selected ?? null));
    close();
  }

  function close() {
    setIsOpen(false);
    setIsWheelOpen(false);
    dragStartY.current = null;
  }

  function move(delta: number) {
    setTempIdx((i) => Math.max(0, Math.min(allOptions.length - 1, i + delta)));
  }

  // Lock body scroll when wheel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Click-outside -> cancel
  useEffect(() => {
    if (!isOpen) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowDown") { e.preventDefault(); move(1); }
      else if (e.key === "ArrowUp") { e.preventDefault(); move(-1); }
      else if (e.key === "Enter") confirm(tempIdx);
      else if (e.key === "Escape") close();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, tempIdx, allOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  function onWheel(e: React.WheelEvent) {
    e.preventDefault();
    move(e.deltaY > 0 ? 1 : -1);
  }

  function getItemHeightPx() {
    if (!rootRef.current) return 72;
    return parseFloat(getComputedStyle(rootRef.current).fontSize) * ITEM_EM;
  }

  function onMouseDown(e: React.MouseEvent) {
    dragStartY.current = e.clientY;
    dragStartIdx.current = tempIdx;
  }

  function onMouseMove(e: React.MouseEvent) {
    if (dragStartY.current === null) return;
    const dy = dragStartY.current - e.clientY;
    const steps = Math.round(dy / getItemHeightPx());
    setTempIdx(Math.max(0, Math.min(allOptions.length - 1, dragStartIdx.current + steps)));
  }

  function onTouchStart(e: React.TouchEvent) {
    dragStartY.current = e.touches[0].clientY;
    dragStartIdx.current = tempIdx;
  }

  function onTouchMove(e: React.TouchEvent) {
    if (dragStartY.current === null) return;
    const dy = dragStartY.current - e.touches[0].clientY;
    const steps = Math.round(dy / getItemHeightPx());
    setTempIdx(Math.max(0, Math.min(allOptions.length - 1, dragStartIdx.current + steps)));
  }

  const containerH = `${VISIBLE * ITEM_EM}em`;
  const translateY = `calc(${-(tempIdx - 2)} * ${ITEM_EM}em)`;

  return (
    <div ref={rootRef} className={className} style={{ userSelect: "none", position: "relative" }}>
      {!isOpen ? (
        <>
          <div style={{ overflow: "hidden", position: "relative" }}>
            <AnimatePresence mode="popLayout">
              <motion.h1
                key={displayText}
                onClick={open}
                style={{ fontSize: fsStyle, cursor: "pointer", color: activeFilter ? "var(--uforeground2)" : undefined, margin: 0 }}
                initial={{ y: "100%", opacity: 0 }}
                animate={{ y: 0, opacity: 1, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } }}
                exit={{ y: "-100%", opacity: 0, transition: { duration: 0.18, ease: "easeIn" } }}
                whileHover={{ scale: 1.05, transition: { type: "spring", stiffness: 300, damping: 20 } }}
              >
                {displayText}
              </motion.h1>
            </AnimatePresence>
          </div>
          <p style={{
            fontSize: "clamp(0.65rem, 1.5vw, 0.8rem)",
            color: "var(--foreground2)",
            marginTop: "0.35em",
            lineHeight: 1.4,
            opacity: 0.75,
          }}>
            {"Click above to filter by role! "}
            <span style={{ fontStyle: "italic" }}>{"Set to " + noFilterLabel + " for no filter."}</span>
          </p>
        </>
      ) : (
        <div
          style={{ fontSize: fsStyle, height: containerH, overflow: "hidden", position: "relative", cursor: "grab", width: "100%" }}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={() => { dragStartY.current = null; }}
          onMouseLeave={() => { dragStartY.current = null; }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={() => { dragStartY.current = null; }}
        >
          <div style={{ transform: "translateY(" + translateY + ")", transition: "transform 0.18s cubic-bezier(0.25, 0.46, 0.45, 0.94)" }}>
            {allOptions.map((name, i) => {
              const dist = Math.abs(i - tempIdx);
              const isCurrent = dist === 0;
              const isNoFilter = i === 0;
              return (
                <div
                  key={i === 0 ? `__nofilter__` : name}
                  onClick={() => confirm(i)}
                  style={{
                    height: ITEM_EM + "em",
                    lineHeight: ITEM_EM + "em",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    width: "100%",
                    opacity: isCurrent ? 1 : dist === 1 ? 0.4 : 0.12,
                    transform: "scale(" + (isCurrent ? 1 : dist === 1 ? 0.82 : 0.64) + ")",
                    transition: "opacity 0.15s, transform 0.15s",
                    cursor: "pointer",
                    color: isCurrent
                      ? (isNoFilter ? "var(--foreground2)" : "var(--uforeground2)")
                      : "var(--foreground)",
                    fontWeight: isCurrent ? 600 : 400,
                    fontStyle: isNoFilter ? "italic" : "normal",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {name}
                </div>
              );
            })}
          </div>

          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "36%", background: "linear-gradient(to bottom, var(--background) 0%, transparent 100%)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "36%", background: "linear-gradient(to top, var(--background) 0%, transparent 100%)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", top: (2 * ITEM_EM) + "em", left: 0, right: 0, height: ITEM_EM + "em", borderTop: "1px solid var(--uforeground2)", borderBottom: "1px solid var(--uforeground2)", opacity: 0.25, pointerEvents: "none", borderRadius: 6 }} />
        </div>
      )}
    </div>
  );
};

export default WheelSelect;
